import { DatabaseIcon } from "lucide-react";
import {
  Action,
  Priority,
  VisualState,
  createAction,
  useKBar,
  useRegisterActions,
} from "kbar";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  GetRawViewsQuery,
  ListModelFoldersQuery,
  ModelListItemFragment,
  useGetRawViewsQuery,
  usePublishedModelsQuery,
  useSavedDraftModelsQuery,
} from "@/apollo/types";
import { useOpenModelInTab } from "@/features/model-tool";
import { useViewSchemaSidebar } from "@/pages/ModelTool/ModelEditorStore";
import { ModelIcon } from "@/pages/ModelTool/components/ModelIcon/ModelIcon";
import { useModelFolders } from "@/pages/ModelTool/hooks/useModelFolder";
import { useRevealInSidebar } from "@/pages/ModelTool/Sidebar/reveal/useRevealInSidebar";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useLoading } from "@/providers/LoadingProvider";
import { useCurrentAccount } from "@/providers/account";
import dayjs from "@/utils/date";
import { useMatchRoute, useNavigate } from "@tanstack/react-location";

function useModelActions(isReadyToLoadData: boolean) {
  const { data: publishedModelsData, loading: isLoadingModels } =
    usePublishedModelsQuery({
      skip: !isReadyToLoadData,
    });

  const publishedModels = publishedModelsData?.publishedModels;

  const { data: draftsData, loading: isLoadingDrafts } =
    useSavedDraftModelsQuery({
      skip: !isReadyToLoadData,
    });
  const savedDraftModels = draftsData?.savedDrafts;

  const { modelFolders, loading: loadingModelFolders } = useModelFolders({
    skip: !isReadyToLoadData,
  });

  useLoading(isLoadingModels || isLoadingDrafts || loadingModelFolders);

  const navigate = useNavigate();

  const { slug } = useCurrentAccount();

  const matchRoute = useMatchRoute();
  const viewSchemaSidebar = useViewSchemaSidebar();

  const openModelInTab = useOpenModelInTab();
  const revealInSidebar = useRevealInSidebar();

  /**
   * Two explicit calls rather than folding reveal into the open: reveal is queued and must never
   * navigate on its own. `focus` stays off — the user is heading for the editor, not the tree.
   */
  const openAndReveal = useCallback(
    (modelId: string, kind: "model" | "draftModel") => {
      openModelInTab({ modelId });
      revealInSidebar({ kind, id: modelId });
    },
    [openModelInTab, revealInSidebar],
  );

  const getModelName = (model: ModelListItemFragment) => {
    if (model.name) return model.name;
    return dayjs(model.createdAt).format("YYYY-MM-DD HH:mm");
  };

  const getModelFolderName = (
    model: ModelListItemFragment,
    folders: ListModelFoldersQuery["listModelFolders"],
  ) => {
    if (!model.publishedQueryId) {
      return "Unpublished";
    }
    const folder = model.folder;
    if (folder) {
      if (folder.parentId) {
        return `${folders.find((f) => f.id === folder.parentId)?.name} / ${
          folder.name
        }`;
      }
      return folder.name;
    }
    return "";
  };

  const actions = useMemo(() => {
    const actions: Action[] = [];

    const publishedModelsSortFn = (
      a: ModelListItemFragment,
      b: ModelListItemFragment,
    ) => {
      //if both have a published query, sort by publishedQuery.createdAt timestamp
      if (a.publishedQuery && b.publishedQuery) {
        return (
          new Date(b.publishedQuery.createdAt).getTime() -
          new Date(a.publishedQuery.createdAt).getTime()
        );
      }
      //sort by has a published query,
      if (a.publishedQuery) return -1;
      if (b.publishedQuery) return 1;
      //if both have no published query, sort by createdAt
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    };

    publishedModels
      ?.slice()
      .sort(publishedModelsSortFn)
      .forEach((model) => {
        actions.push({
          id: model.id,
          name: getModelName(model),
          keywords: "",
          subtitle: getModelFolderName(model, modelFolders),
          priority: Priority.HIGH,
          section: "Models",
          icon: <ModelIcon model={model} />,
        });

        actions.push(
          createAction({
            parent: model.id,
            name: "View model",
            keywords: model.name,
            priority: Priority.LOW,
            perform: () => {
              openAndReveal(model.id, "model");
            },
            section: "",
          }),
        );
        actions.push(
          createAction({
            parent: model.id,
            name: "View schema",
            priority: Priority.LOW,

            keywords: model.name,
            perform: () => {
              if (
                !matchRoute({
                  to: `/${slug}/editor`,
                  fuzzy: true,
                })
              ) {
                navigate({ to: `/${slug}/editor` });
              }
              viewSchemaSidebar({
                id: model.id,
                type: "model",
              });
            },
            section: "",
          }),
        );
      });

    savedDraftModels
      ?.slice()
      .sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })
      .forEach((model) => {
        actions.push({
          id: model.id,
          name: getModelName(model),
          keywords: "",
          priority: Priority.HIGH,
          section: "Saved Drafts",
          icon: <ModelIcon model={model} />,
          perform: () => {
            openAndReveal(model.id, "draftModel");
          },
        });
      });

    return actions;
  }, [
    matchRoute,
    modelFolders,
    publishedModels,
    savedDraftModels,
    navigate,
    openAndReveal,
    viewSchemaSidebar,
    slug,
  ]);

  useRegisterActions(actions, [
    publishedModels,
    savedDraftModels,
    modelFolders,
  ]);
}

function useRawViewActions(isReadyToLoadData: boolean) {
  const { data, loading } = useGetRawViewsQuery({
    skip: !isReadyToLoadData,
  });
  const rawViews = data?.getRawViews ?? [];

  useLoading(loading);

  const viewSchemaSidebar = useViewSchemaSidebar();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { slug } = useCurrentAccount();

  const getViewPath = (view: GetRawViewsQuery["getRawViews"][0]) => {
    const [, ...path] = view.weldTag.split(".");
    path.pop();
    return path.join(".");
  };

  useRegisterActions(
    rawViews.map((view) => ({
      id: view.viewId,
      name: view.weldTag.split(".")[view.weldTag.split(".").length - 1],
      keywords: "",
      subtitle: getViewPath(view),
      priority: Priority.NORMAL,
      icon: <DatabaseIcon className="size-4" />,
      perform: () => {
        viewSchemaSidebar({
          id: view.viewId,
          type: "rawView",
        });
        if (
          !matchRoute({
            to: `/${slug}/editor`,
            fuzzy: true,
          })
        ) {
          navigate({ to: `/${slug}/editor` });
        }
      },
      section: "Raw Data",
    })),
    [rawViews],
  );
}

/**
 * Will return true once the KBar has been opened and will continue to return to true thereafter
 */
function useIsReadyToLoadData() {
  const { isKBarOpen } = useKBar((state) => ({
    isKBarOpen: state.visualState === VisualState.showing,
  }));
  const [isReady, setIsReady] = useState(isKBarOpen);
  useEffect(() => {
    if (isKBarOpen) {
      setIsReady(true);
    }
  }, [isKBarOpen]);
  return isReady;
}

export default function useModelToolActions() {
  let isReady = useIsReadyToLoadData();
  const dwh = useDataWarehouseContext();
  isReady = dwh.features.isTransformEnabled ? isReady : false;
  useModelActions(isReady);
  useRawViewActions(isReady);
}
